// src/services/orderSync.ts
// Fetches orders (notifications) and customer messages from the backend API
// and writes them into IndexedDB, replacing any previously synced data.

import { fetchWithAuth } from './auth';
import type { Notification } from '../types/Notification';
import type { QuestionMessage } from '../types/QuestionMessage';
import { API_BASE } from '../config';

// ─── mappers ──────────────────────────────────────────────────────────────────

type OrderedProductRow = {
  product_id?: number;
  phone?: string;
  sum?: number;
  status?: string;
  created_at?: string;
  model?: string;
  category_name?: string;
  product_type?: string;
  detail_number?: string;
  count?: number;
  product_price?: number;
  admin_discount?: number;
};

// Backend returns one row per ordered product. Group by phone so each unique
// customer order becomes one Notification with aggregated categories/types.
function groupOrderRows(rows: OrderedProductRow[]): Notification[] {
  const byPhone = new Map<string, OrderedProductRow[]>();
  for (const row of rows) {
    const phone = String(row.phone ?? '');
    if (!byPhone.has(phone)) byPhone.set(phone, []);
    byPhone.get(phone)!.push(row);
  }

  const notifications: Notification[] = [];
  for (const [phone, group] of byPhone) {
    const firstRow = group[0];
    const createdAt = firstRow.created_at
      ? new Date(firstRow.created_at).toISOString()
      : new Date().toISOString();

    const total = group.reduce((acc, r) => acc + Number(r.sum ?? 0), 0);
    const adminDiscount = Number(group[0]?.admin_discount ?? 0);
    const effectiveTotal = total * (1 - adminDiscount / 100);
    const categories = [...new Set(group.map(r => r.category_name).filter(Boolean))] as string[];
    const productTypes = [...new Set(group.map(r => r.product_type).filter(Boolean))] as string[];
    const skus = group.map(r => r.detail_number).filter(Boolean) as string[];

    const items = group.map((r, idx) => ({
      id: `${phone}_${idx}`,
      name: r.detail_number || r.model || '—',
      type: (r.product_type === 'original' ? 'original' : 'copy') as 'original' | 'copy',
      qty: Number(r.count ?? 1),
      price: Number(r.product_price ?? 0),
      total: Number(r.sum ?? 0),
    }));

    // Use most recent/relevant status from the group
    const rowStatus = firstRow.status ?? 'pending';
    const STATUS_MAP: Record<string, string> = {
      pending:  'new',
      sold:     'shipped',
      canceled: 'canceled',
      return:   'returned',
    };
    const notifStatus = STATUS_MAP[rowStatus] ?? 'new';

    notifications.push({
      id: `${phone}_${createdAt}`,
      number: phone,
      customer: phone,
      phone,
      createdAt,
      total,
      discount: adminDiscount,
      effectiveTotal,
      currency: 'AMD',
      status: notifStatus,
      isRead: false,
      categories,
      productTypes,
      skus,
      items,
    } as any);
  }
  return notifications;
}

type OnorderRow = {
  id?: number | string;
  name?: string;
  phone?: string;
  message?: string;
  status?: string;
  created_at?: string;
};

function mapMessageRow(row: OnorderRow): QuestionMessage {
  return {
    id:        String(row.id ?? ''),
    name:      String(row.name ?? ''),
    phone:     String(row.phone ?? ''),
    message:   String(row.message ?? ''),
    createdAt: row.created_at
      ? new Date(row.created_at).toISOString()
      : new Date().toISOString(),
    isRead:    row.status === 'accepted',
  };
}

// ─── fetch functions (return mapped arrays; state lives in the collection stores) ──

export async function fetchNotifications(): Promise<Notification[]> {
  const res = await fetchWithAuth(`${API_BASE}/order/notifications/pending`);
  if (!res.ok) throw new Error(`Ошибка при загрузке уведомлений: ${res.status}`);

  const data = await res.json() as { orderedProducts?: OrderedProductRow[] };
  return groupOrderRows(data.orderedProducts ?? []);
}

export async function updateOrderStatus(phones: string[], status: 'canceled' | 'return' | 'sold' | 'pending'): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/order/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phones, status }),
  });
  if (!res.ok) throw new Error(`Ошибка смены статуса: ${res.status}`);
}

export async function markOrdersSold(phones: string[]): Promise<void> {
  if (!phones.length) return;
  // Just update status to sold, don't delete from DB
  await updateOrderStatus(phones, 'sold');
}

export async function markOrderSold(phone: string): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/order/sold`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: 'pending', items: [phone] }),
  });
  if (!res.ok) throw new Error(`Ошибка при изменении статуса: ${res.status}`);
}

export async function setOrderDiscount(phone: string, discount: number): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/order/discount`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, discount }),
  });
  if (!res.ok) throw new Error(`Ошибка сохранения скидки: ${res.status}`);
}

export async function deleteMessageById(id: string): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/order/message/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Ошибка удаления сообщения: ${res.status}`);
}

// ─── offline (point-of-sale) sale ───────────────────────────────────────────────

export type OfflineSaleLine = {
  product_id: number;
  product_type: 'original' | 'copy';
  count: number;
};

export type OfflineSaleResult = {
  ok: boolean;
  ref: string;
  total: number;
  count: number;
  payment_method: 'cash' | 'card';
};

export async function createOfflineSale(payload: {
  lines: OfflineSaleLine[];
  phone?: string;
  payment_method: 'cash' | 'card';
}): Promise<OfflineSaleResult> {
  const res = await fetchWithAuth(`${API_BASE}/order/offline-sale`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let msg = `Ошибка оформления продажи: ${res.status}`;
    try {
      const e = await res.json();
      if (e?.message) msg = e.message;
    } catch { /* keep default */ }
    throw new Error(msg);
  }
  return res.json() as Promise<OfflineSaleResult>;
}

export type OfflineSaleRow = {
  id: number;
  product_id: number;
  product_type: 'original' | 'copy';
  count: number;
  product_price: number;
  sum: number;
  payment_method: 'cash' | 'card';
  ref: string;
  created_at: string;
  brand: string;
  model: string;
  detail_number: string;
};

export async function fetchOfflineSales(): Promise<OfflineSaleRow[]> {
  const res = await fetchWithAuth(`${API_BASE}/order/offline-sales`);
  if (!res.ok) throw new Error(`Ошибка загрузки истории продаж: ${res.status}`);
  const rows = await res.json() as any[];
  return rows.map((r) => ({
    id:            Number(r.id),
    product_id:    Number(r.product_id),
    product_type:  r.product_type === 'copy' ? 'copy' : 'original',
    count:         Number(r.count ?? 0),
    product_price: Number(r.product_price ?? 0),
    sum:           Number(r.sum ?? 0),
    payment_method: r.payment_method === 'card' ? 'card' : 'cash',
    ref:           String(r.ref ?? ''),
    created_at:    r.created_at ? new Date(r.created_at).toISOString() : new Date().toISOString(),
    brand:         String(r.brand ?? ''),
    model:         String(r.model ?? ''),
    detail_number: String(r.detail_number ?? ''),
  }));
}

export async function deleteOfflineSaleLine(id: number): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/order/offline-sale/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Ошибка удаления продажи: ${res.status}`);
}

export async function updateOfflineSaleLine(
  id: number,
  data: { count?: number; payment_method?: 'cash' | 'card' },
): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/order/offline-sale/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Ошибка изменения продажи: ${res.status}`);
}

export async function fetchMessages(): Promise<QuestionMessage[]> {
  const res = await fetchWithAuth(`${API_BASE}/order/messages/pending`);
  if (!res.ok) throw new Error(`Ошибка при загрузке сообщений: ${res.status}`);

  const rows = await res.json() as OnorderRow[];
  return rows.map(mapMessageRow);
}
